import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import FactionMpProvider from "@/core/data-provider/data-mp/faction/faction-mp-provider";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import toast from "@/domains/toasts/helpers/toast";
import { QUERY_KEY_FACTION_GENERAL_INFO } from "@/domains/faction/hooks/useGetInfo";
import { QUERY_KEY_FACTION_COLLEAGUES_INFO } from "@/domains/faction/hooks/useGetParticipants";

type KnownErr = Error & { knownI18nKey?: boolean };
type Vars = { memberId: number; direction: 1 | -1; cb?: () => void };

export function useUpDownRank() {
  const { t, i18n } = useTranslation("faction");
  const queryClient = useQueryClient();

  return useMutation<void, KnownErr, Vars>({
    mutationFn: async ({ memberId, direction }) => {
      const response = await FactionMpProvider.changeMemberRank(memberId, direction);

      if (response instanceof RpcMpProviderFailedResponse) {
        const messageKey = response?.error?.message ?? "Unknown";
        const knownI18nKey = i18n.exists(`faction:faction.errors.${messageKey}`);
        const error = new Error(messageKey) as KnownErr;
        error.knownI18nKey = knownI18nKey;
        throw error;
      }
    },
    onSuccess: async (_data, variables) => {
      if (variables?.direction === 1) {
        toast.success(t("faction.toast.upRankSuccess"));
      } else {
        toast.success(t("faction.toast.downRankSuccess"));
      }
      variables?.cb?.();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_FACTION_GENERAL_INFO] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_FACTION_COLLEAGUES_INFO] }),
      ]);
    },
    onError: (err, variables) => {
      const error = err as KnownErr;
      console.error(error);

      const direction = variables?.direction;

      if (error.knownI18nKey) {
        toast.error(t(`faction.errors.${error.message}`));
      } else if (direction === 1) {
        toast.error(t("faction.toast.upRankError"));
      } else {
        toast.error(t("faction.toast.downRankError"));
      }
    },
  });
}
